import { FC, useReducer, useState } from 'react'
import { Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../context/auth-context'
import { EmployeeDisplayTinyInfo, MeetingType } from '../../../types'
import { ValidationErrorsData } from '../../../shared/graphqlErrorHelper'
import { PerModal } from '../Modal'
import { EmployeeSelect } from '../InviteModalContent/EmployeeSelect'
import { InviteModalRow } from '../InviteModalContent/InviteModalRow'
import { MeetingInviteOptionsSelect } from '../InviteModalContent/MeetingInviteOptionsSelect'
import useRequestMeeting from '../../../graphql/meeting/hooks/useRequestMeeting'
import { MeetingInviteOptionsExtended } from '../../../../../integration-tests/common/types'
import { showNotification } from '../Notification/notification'
import { useMeetingRequestOptionsStore } from '../../../graphql/requestMeetingOptions/MeetingRequestOptionsStore'
import PerText from '../Text'

const { TextArea } = Input

type RequestModalState = {
  reviewer: EmployeeDisplayTinyInfo
  meetingType: MeetingType
  reason: string
  requestTypeOptions: MeetingInviteOptionsExtended
  serverError?: ValidationErrorsData[]
}

type ReducerActionType = {
  type: 'reviewer' | 'meetingType' | 'reason' | 'requestTypeOptions' | 'setError' | 'reset'
  value?: EmployeeDisplayTinyInfo | MeetingType | MeetingInviteOptionsExtended | ValidationErrorsData[] | string | undefined
  serverError?: ValidationErrorsData[]
}

type EmployeeRequestMeetingModalProps = {
  visible: boolean
  closeModal: () => void
  reviewerList: EmployeeDisplayTinyInfo[]
  meetingOptions: MeetingInviteOptionsExtended
  context: 'OwnGrowth' | 'MyMeetings'
}

export const EmployeeRequestMeetingModal: FC<EmployeeRequestMeetingModalProps> = ({
  reviewerList,
  visible,
  closeModal,
  meetingOptions,
  context,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { requestMeeting } = useRequestMeeting()
  const [isProcessing, toggleProcessing] = useState(false)

  // There will always be at least one option available because the modal is only visible in this case
  const getFirstMeetingOptionAvailable = () => {
    if (context === 'OwnGrowth') {
      return [...meetingOptions.possible].find((option) => [MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(option))!
    } else {
      const sortedOptions = [...meetingOptions.possible].sort((a, b) => a.localeCompare(b))
      const appraisalDisabled = meetingOptions.tagAssignmentIsInProgress || !meetingOptions.isAppraisalEnabled
      return appraisalDisabled
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          sortedOptions.find((option) => ![MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(option))!
        : sortedOptions[0]
    }
  }

  const initialState: RequestModalState = {
    reviewer: reviewerList[0],
    reason: '',
    meetingType: getFirstMeetingOptionAvailable(),
    requestTypeOptions: {
      possible:
        context === 'OwnGrowth'
          ? [[...meetingOptions.possible].find((option) => [MeetingType.Appraisal, MeetingType.AppraisalUpdate].includes(option))!]
          : meetingOptions.possible,
      tagAssignmentIsInProgress: meetingOptions.tagAssignmentIsInProgress,
      isAppraisalEnabled: meetingOptions.isAppraisalEnabled,
    },
    serverError: undefined,
  }

  const reducer = (actState: RequestModalState, action: ReducerActionType) => {
    const nextState = { ...actState }
    switch (action.type) {
      case 'reviewer':
        nextState.reviewer = action.value as EmployeeDisplayTinyInfo
        break
      case 'meetingType':
        nextState.meetingType = action.value as MeetingType
        break
      case 'reason':
        nextState.reason = action.value as string
        break
      case 'requestTypeOptions':
        nextState.requestTypeOptions = action.value as MeetingInviteOptionsExtended
        break
      case 'setError':
        nextState.serverError = action.value as ValidationErrorsData[] | undefined
        break
      case 'reset':
        return { ...initialState }
      default:
        throw new Error()
    }
    return nextState
  }
  const [state, dispatch] = useReducer(reducer, initialState)

  const submit = async () => {
    toggleProcessing(true)
    try {
      const res = await requestMeeting(user.employeeId, state.reviewer.id, state.meetingType, state.reason, context)
      if (res.errors && res.errors[0] && res.errors[0].extensions) {
        dispatch({ type: 'setError', value: res.errors[0].extensions.validationErrors })
      } else {
        const refetchPromise = useMeetingRequestOptionsStore.getState().refetch(user.employeeId)
        await refetchPromise
        showNotification({
          type: 'success',
          message: t('meetingRequest.notifications.title'),
          description: t('meetingRequest.notifications.description', {
            reviewerName: state.reviewer.firstName,
          }),
        })
        closeRequestMeetingModal()
      }
    } finally {
      toggleProcessing(false)
    }
    toggleProcessing(false)
  }

  const closeRequestMeetingModal = () => {
    dispatch({ type: 'reset' })
    closeModal()
  }

  return (
    <PerModal
      visible={visible}
      onCancel={() => closeRequestMeetingModal()}
      size="large"
      title={{
        heading:
          context === 'OwnGrowth'
            ? t(`employeeInvite.headlineAppraisal`, { appraisalType: t(`meetingTypes.${state.meetingType}`) })
            : t(`employeeInvite.headline`),
      }}
      footer={{
        cancelBtn: {
          onClick: async (e) => {
            e.preventDefault() // We should refactor button to not emit native event!
            closeRequestMeetingModal()
          },
        },
        customBtns: [
          {
            text: t(`employeeInvite.request`),
            onClick: async (e) => {
              e.preventDefault() // We should refactor button to not emit native event!
              await submit()
            },
            color: 'primary',
            icon: 'chat_fill',
            disabled: state.reason.length === 0 || (state.serverError && state.serverError.length > 0) || isProcessing,
            tooltip: state.serverError
              ? t('employeeInvite.validation.serverError')
              : state.reason.length === 0
              ? t('employeeInvite.validation.tooltip')
              : undefined,
            isProcessing: { active: isProcessing },
          },
        ],
      }}
    >
      <InviteModalRow label={t('employeeInvite.reviewer')}>
        <EmployeeSelect
          employeeList={reviewerList}
          employee={state.reviewer}
          onChangeEmployee={(reviewer) => dispatch({ type: 'reviewer', value: reviewer })}
        />
      </InviteModalRow>
      <InviteModalRow label={t('employeeInvite.meetingType')}>
        <MeetingInviteOptionsSelect
          onChangeMeetingType={(type) => dispatch({ type: 'meetingType', value: type })}
          inviteOptions={state.requestTypeOptions.possible ? state.requestTypeOptions.possible : []}
          meetingType={state.meetingType}
          isPredefined={context === 'OwnGrowth'}
          shouldDisableAppraisals={state.requestTypeOptions.tagAssignmentIsInProgress || !state.requestTypeOptions.isAppraisalEnabled}
        />
      </InviteModalRow>
      <InviteModalRow label={t('employeeInvite.reason')} align="top">
        <TextArea
          value={state.reason}
          placeholder={t('employeeInvite.reasonPlaceholder')}
          onChange={(event) => dispatch({ type: 'reason', value: event.target.value })}
          rows={4}
        />
      </InviteModalRow>
      {state.serverError && (
        <PerText color="error" fontSize="m" mt="m">
          {state.serverError.some((error) => error.errorKey === 'permission.not.granted.30095')
            ? t('global.notifications.error.description')
            : state.serverError[0].message}
        </PerText>
      )}
    </PerModal>
  )
}
